
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";

interface TrialBannerProps {
  trialEndDate: string;
  onUpgrade?: () => void;
}

export const TrialBanner = ({ trialEndDate, onUpgrade }: TrialBannerProps) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    expired: boolean;
  }>({ days: 0, hours: 0, minutes: 0, expired: false });
  
  const navigate = useNavigate();

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const endTime = new Date(trialEndDate).getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        
        setTimeLeft({ days, hours, minutes, expired: false });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, expired: true });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [trialEndDate]);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/pricing');
    }
  };

  if (timeLeft.expired) {
    return (
      <Card className="p-4 bg-red-50 border-red-200 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">Trial Expired</h3>
              <p className="text-red-700">Your 7-day trial has ended. Please choose a subscription plan to continue.</p>
            </div>
          </div>
          <Button 
            onClick={handleUpgrade}
            className="bg-red-600 hover:bg-red-700"
          >
            Choose Plan
          </Button>
        </div>
      </Card>
    );
  }

  const isUrgent = timeLeft.days <= 1;

  return (
    <Card className={`p-4 mb-6 ${isUrgent ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock className={`w-6 h-6 ${isUrgent ? 'text-orange-600' : 'text-blue-600'}`} />
          <div>
            <h3 className={`text-lg font-semibold ${isUrgent ? 'text-orange-800' : 'text-blue-800'}`}>
              {isUrgent ? 'Trial Ending Soon!' : '🔔 You\'re currently on a 7-Day Free Trial. Your subscription will begin after the trial ends.'}
            </h3>
            <p className={`${isUrgent ? 'text-orange-700' : 'text-blue-700'}`}>
              {timeLeft.days > 0 && `${timeLeft.days} day${timeLeft.days > 1 ? 's' : ''}, `}
              {timeLeft.hours}h {timeLeft.minutes}m remaining in your trial
              {isUrgent && '. Please activate your monthly subscription.'}
            </p>
          </div>
        </div>
        <Button 
          onClick={handleUpgrade}
          variant={isUrgent ? "default" : "outline"}
          className={isUrgent ? "bg-orange-600 hover:bg-orange-700" : ""}
        >
          {isUrgent ? 'Subscribe Now' : 'Upgrade Now'}
        </Button>
      </div>
    </Card>
  );
};
